import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useContentOperations } from "@/hooks/useContentOperations";
import { usePersistentViewMode } from "@/hooks/usePersistentViewMode";
import ContentDisplay from "@/components/dashboard/content-display/ContentDisplay";
import { ContentItem } from "@/components/dashboard/content-card/types";
import CollectionHeader from "@/components/dashboard/CollectionHeader";
import LoadingState from "@/components/dashboard/LoadingState";
import { useCollections } from "@/contexts/collections";
import EditTagDialog from "@/components/tags/EditTagDialog";
import { useHiddenTags } from "@/hooks/useHiddenTags";
import { useTags } from "@/hooks/useTags";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";

const TagEmptyState = ({ message }: { message: string }) => {
  return (
    <div className="text-center py-10 bg-clustr-lightGray/50 rounded-lg">
      <p className="text-clustr-darkGray mb-4">{message}</p>
    </div>
  );
};

const TagPage = () => {
  const { id } = useParams<{ id: string }>();
  const { viewMode, setViewMode } = usePersistentViewMode();
  const { handleContentClick, handleDelete, handleArchive } = useContentOperations();
  const [tagDetails, setTagDetails] = useState<{ name: string; color: string; description?: string } | null>(null);
  const { getCollectionById } = useCollections();
  const { isTagHidden, toggleTagVisibility } = useHiddenTags();
  const [isEditTagOpen, setIsEditTagOpen] = useState(false);
  const [editTagData, setEditTagData] = useState<{id: string, name: string, color: string, description?: string} | null>(null);
  const { user } = useAuth();
  const { updateTag } = useTags();
  const { title, description, image } = APP_METADATA.default;
  useMetadata({ title, description, image });

  useEffect(() => {
    console.log(`[TAG PAGE] Mounted or updated with view mode: ${viewMode}`);
  }, [viewMode]);

  useEffect(() => {
    const fetchTagDetails = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from('tags')
          .select('name, color, description')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching tag details:', error);
          return;
        }

        setTagDetails(data);
      } catch (err) {
        console.error('Exception fetching tag details:', err);
      }
    };

    fetchTagDetails();
  }, [id]);

  const { data: contentItems = [], isLoading, refetch } = useQuery({
    queryKey: ['tagContent', id],
    queryFn: async () => {
      if (!id) return [];

      const { data: contentTags, error: contentTagsError } = await supabase
        .from('content_tags')
        .select('content_id')
        .eq('tag_id', id);

      if (contentTagsError) {
        console.error('Error fetching content tags:', contentTagsError);
        return [];
      }

      if (!contentTags.length) return [];

      const contentIds = contentTags.map(item => item.content_id);

      const { data: content, error: contentError } = await supabase
        .from('content_items')
        .select('*')
        .in('id', contentIds)
        .order('created_at', { ascending: false });

      if (contentError) {
        console.error('Error fetching content:', contentError);
        return [];
      }

      const enhancedContent = await Promise.all(content.map(async (item) => {
        const { data: contentCollection, error: collectionError } = await supabase
          .from('content_collections')
          .select('collection_id')
          .eq('content_id', item.id)
          .maybeSingle();

        if (collectionError || !contentCollection) {
          console.log(`No collection found for content ${item.id}, using default color`);
          return { ...item, collection_color: "#6B7789" };
        }

        const collectionId = contentCollection.collection_id;
        
        const collectionFromContext = getCollectionById(collectionId);
        if (collectionFromContext) {
          return { 
            ...item, 
            collection_color: collectionFromContext.color || "#6B7789",
            collection_id: collectionId
          };
        }

        const { data: collection, error: fetchError } = await supabase
          .from('collections')
          .select('color')
          .eq('id', collectionId)
          .single();

        if (fetchError || !collection) {
          console.log(`Error fetching collection for content ${item.id}, using default color`);
          return { 
            ...item, 
            collection_color: "#6B7789",
            collection_id: collectionId
          };
        }

        return { 
          ...item, 
          collection_color: collection.color || "#6B7789",
          collection_id: collectionId
        };
      }));

      return enhancedContent as ContentItem[];
    },
    enabled: !!id,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    const handleTagVisibilityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const updatedHiddenTags = customEvent.detail;
      
      if (id && updatedHiddenTags && (id in updatedHiddenTags)) {
        console.log("[TAG PAGE] Tag visibility changed for current tag, refreshing data");
        refetch();
      }
    };

    window.addEventListener('hiddenTagsChanged', handleTagVisibilityChange);
    return () => {
      window.removeEventListener('hiddenTagsChanged', handleTagVisibilityChange);
    };
  }, [id, refetch]);

  const handleEditContent = (contentId: string, title: string) => {
    console.log(`Editing content: ${contentId}, ${title}`);
  };

  const handleViewModeChange = (newMode: typeof viewMode) => {
    console.log(`[TAG PAGE] Changing view mode from ${viewMode} to ${newMode}`);
    
    console.log("[TAG PAGE] Forcing view refresh");
    setTimeout(() => {
      setViewMode(newMode);
    }, 10);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-none">
        <CollectionHeader
          title={tagDetails?.name || "Tag Content"}
          description={tagDetails?.description || "Content with this tag"}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </div>

      <div className={`flex-1 ${viewMode === 'freeform' || viewMode === 'network' ? '' : 'px-7'} overflow-auto`}>
        {contentItems.length > 0 ? (
          <ContentDisplay
            key={`tag-content-${viewMode}`}
            contentItems={contentItems}
            viewMode={viewMode}
            onContentClick={handleContentClick}
            onDeleteContent={handleDelete}
            onArchiveContent={handleArchive}
            onEditContent={handleEditContent}
            collectionColor={tagDetails?.color}
            storeNodePositionsInTag={id}
          />
        ) : (
          <TagEmptyState message="No content with this tag yet" />
        )}
      </div>
      
      <EditTagDialog
        open={isEditTagOpen}
        onOpenChange={setIsEditTagOpen}
        tagId={editTagData?.id}
        initialData={editTagData ? {
          name: editTagData.name,
          description: editTagData.description,
          color: editTagData.color
        } : undefined}
      />
    </div>
  );
};

export default TagPage;
